/* eslint-disable max-lines */
import React, { useState } from 'react';
import { ColumnList, DataField, FormField } from '../../interfaces/AppProps';
import generateRows from '../../utils/generateRows';
import { Button, Card, Input, Select, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import convertToDMN from '../../utils/convertToDmn';
import estimateRowCount from '../../utils/estimateRowCount';

const { Option } = Select
type FormProps = {
  inputRefs: any;
  inputEntryFormFields: FormField[];
  setInputEntryFormFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  outputEntryFormFields: FormField[];
  setOutputEntryFormFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  setDmnData: React.Dispatch<DataField>;
  columnList?: ColumnList,
  setHeaderSequence: any,
  setXmlStructure: any
};

type FieldError = {
  [key: string]: string;
};

const Form = ({
  inputRefs,
  inputEntryFormFields,
  setInputEntryFormFields,
  outputEntryFormFields,
  setOutputEntryFormFields,
  setDmnData,
  columnList,
  setHeaderSequence,
  setXmlStructure
}: FormProps) => {
  // const [inputErrors, setInputErrors] = useState([]);
  // const [outputErrors, setOutputErrors] = useState([]);
  const [inputErrors, setInputErrors] = useState<FieldError[]>([]);
  const [outputErrors, setOutputErrors] = useState<FieldError[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);


  const handleFormInputChange = (
    index: number,
    event: any
  ) => {
    const { name, type, value } = event.target;
    const values = [...inputEntryFormFields];

    if (type === 'checkbox') {
      values[index] = {
        ...values[index],
        [name]: (event.target as HTMLInputElement).checked,
      };
    } else {
      values[index] = {
        ...values[index],
        [name]: value,
      };
    }

    setInputEntryFormFields(values);
  };


  const handleAddInputEntryField = () => {
    setInputEntryFormFields([
      ...inputEntryFormFields,
      {
        name: '',
        type: '',
        start: '',
        end: '',
        includeStart: false,
        interval: '',
      },
    ]);
  };

  const handleRemoveInputField = (index: number) => {
    const updatedOutputFields = [...inputEntryFormFields];
    updatedOutputFields.splice(index, 1);
    setInputEntryFormFields(updatedOutputFields);
  };

  const handleOutputFieldChange = (
    index: number,
    event: any
  ) => {
    const { name, value } = event.target;
    const updatedOutputFields = [...outputEntryFormFields];
    updatedOutputFields[index] = {
      ...updatedOutputFields[index],
      [name]: value,
    };
    setOutputEntryFormFields(updatedOutputFields);
  };

  const handleAddOutputField = () => {
    setOutputEntryFormFields([
      ...outputEntryFormFields,
      { name: '', type: '' },
    ]);
  };

  const handleRemoveOutputField = (index: number) => {
    const updatedOutputFields = [...outputEntryFormFields];
    updatedOutputFields.splice(index, 1);
    setOutputEntryFormFields(updatedOutputFields);
  };

  const validateFields = () => {
    let isValid = true;
    const newInputErrors = Array(inputEntryFormFields.length).fill(null).map(() => ({}));
    const newOutputErrors = Array(outputEntryFormFields.length).fill(null).map(() => ({}));

    inputEntryFormFields.forEach((field, index) => {
      const errors: { [key: string]: string } = {};

      if (!field.name) errors.name = 'Name is required.';
      if (!field.type) errors.type = 'Type is required.';
      if (field.type !== 'string' && !field.start) errors.start = 'Start value is required.';
      if (field.type !== 'string' && !field.end) errors.end = 'End value is required.';
      if (field.type !== 'string' && isNaN(Number(field.start))) errors.start = 'Start must be a number.';
      if (field.type !== 'string' && isNaN(Number(field.end))) errors.end = 'End must be a number.';

      // New validation rules
      if (field.type !== 'string') {
        const startValue = Number(field.start);
        const endValue = Number(field.end);
        const intervalValue = Number(field.interval);

        if (endValue < startValue) {
          errors.end = 'End value cannot be smaller than start value.';
        }
        if (intervalValue > (endValue - startValue)) {
          errors.interval = 'Interval cannot be more than the difference between start and end values.';
        } 
        if(intervalValue === endValue){
          errors.interval = 'Interval cannot be same as end value';
        } 
        // if(intervalValue === startValue){
        //   errors.interval = 'Interval cannot be same as start value'
        // }
        if(intervalValue > endValue){
          errors.interval = 'Interval cannot be more than end value';
        } 
        if(intervalValue === 0){
          errors.interval = 'Interval cannot be zero'
        }
      }

      if (field.type !== 'string' && field.interval === '') {
        errors.interval = 'Interval is required.';
      } else if (field.type !== 'string' && isNaN(Number(field.interval))) {
        errors.interval = 'Interval must be a number.';
      }

      if (Object.keys(errors).length > 0) isValid = false;
      newInputErrors[index] = errors;
    });

    outputEntryFormFields.forEach((field, index) => {
      const errors: { [key: string]: string } = {};

      if (!field.name) errors.name = 'Name is required.';
      if (!field.type) errors.type = 'Type is required.';

      if (Object.keys(errors).length > 0) isValid = false;
      newOutputErrors[index] = errors;
    });

    setInputErrors(newInputErrors);
    setOutputErrors(newOutputErrors);
    return isValid;
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!validateFields()) {
      return;
    }
    const updatedData: any = inputEntryFormFields.map(
      (field: FormField, index: number) => {
        const inputRef = inputRefs.current[index];
        if (inputRef) {
          const updatedField = {
            ...field,
          };
          return updatedField;
        }
        return field;
      }
    );
    setInputEntryFormFields(updatedData);
    // Estimate row count before generating
    const estimatedRows = estimateRowCount(updatedData);
    setRowCount(estimatedRows);
    if (estimatedRows > 200) {
      message.error('You can generate a maximum of 200 rows. Please adjust your input.');
      return;
    }
    const formData = {
      inputFields: updatedData.map((field: FormField) => ({
        name: field.name,
        type: field.type,
      })),
      outputFields: outputEntryFormFields.map((field: FormField) => ({
        name: field.name,
        type: field.type,
      })),
      data: generateRows(updatedData, outputEntryFormFields),
    };
    // setRowCount(formData.data.length); // No longer needed, we use the estimate
    console.log(formData, 'formData');
    setDmnData(formData);
    setHeaderSequence(Object.keys(formData?.data[0]))
    const dummyXmString = convertToDMN(formData, 'newRule');
    setXmlStructure(dummyXmString)
    console.log('Submitted Data:', updatedData);
  };
  return (
    <div className="dmn-form">
      <div className="form-card m-0">
        <div className='m-0'>
          <h4 className='form-title'>Input Columns</h4>
          {inputEntryFormFields.map((field, index) => (
            <div className="dmn-form-container" key={index}>
              <div className="flex dmn-form-row">
                <div className="">
                  <div className="label">
                    Name
                  </div>
                  <Select
                    className="select-menu"
                    size='large'
                    showSearch
                    value={field.name || undefined}
                    onChange={(value) => {
                      handleFormInputChange(index, { target: { value } });
                      const selectedOption = columnList?.find((option) => option.name === value);
                      if (selectedOption) {
                        const updatedFields = [...inputEntryFormFields];
                        updatedFields[index].name = value;
                        updatedFields[index].type = selectedOption.type;
                        setInputEntryFormFields(updatedFields);
                      }

                    }}
                    placeholder="Select an option"
                  >
                    {columnList?.map((option, idx) => (
                      <Option key={idx} value={option.name}>
                        {option.name}
                      </Option>
                    ))}
                  </Select>
                  {inputErrors[index]?.name ? (
                    <div className="error-message">
                      {inputErrors[index].name || ''}
                    </div>
                  ) : null}
                  {/* {inputErrors[index]?.name && <div className="error-message">{inputErrors[index].name}</div>} */}
                </div>

                <div >

                  <div className="label">
                    {field.type !== 'string' ? "Start Value" : "Possible Values (comma separated)"}
                  </div>
                  <Input
                    className={field.type !== 'string' ? "input-number-field" : "input-field-style"}
                    type="text"
                    name="start"
                    value={field.start}
                    onChange={(event) => handleFormInputChange(index, event)}
                    placeholder={field.type !== 'string' ? "Enter start value" : "Enter possible values"}
                  />
                  {inputErrors[index]?.start ? (
                    <div className="error-message">
                      {inputErrors[index].start || ''}
                    </div>
                  ) : null}
                  {/* {inputErrors[index]?.start && <div className="error-message">{inputErrors[index].start}</div>} */}
                </div>
                {field.type !== 'string' ? (
                  <div className="">
                    <div className="label">
                      End Value
                    </div>
                    <Input
                      className={field.type !== 'string' ? "input-number-field" : "input-field-style"}
                      type="text"
                      name="end"
                      value={field.end}
                      onChange={(event) => handleFormInputChange(index, event)}
                      placeholder="Enter end value"
                    />
                    {inputErrors[index]?.end ? (
                      <div className="error-message">
                        {inputErrors[index].end || ''}
                      </div>
                    ) : null}
                    {/* {inputErrors[index]?.end && <div className="error-message">{inputErrors[index].end}</div>} */}
                  </div>
                ) : null}
                <div>
                  <label className="checkbox-container-label">
                    <input
                      className="dmr-2"
                      type="checkbox"
                      name="includeStart"
                      checked={field.includeStart}
                      onChange={(event) => handleFormInputChange(index, event)}
                    />
                    {/* create separate entries for each item  for string*/}
                    <span className='checkbox-label'>{field.type !== 'string' ? "Include Start Value" : "Create Separate Entries For Each Item For String"}</span>
                  </label>
                </div>
                {field.type !== 'string' ? (
                  <div className="">
                    <div className="label">
                      Increment By
                    </div>
                    <Input
                      className={field.type !== 'string' ? "input-number-field" : "input-field-style"}
                      type="text"
                      name="interval"
                      value={field.interval}
                      onChange={(event) => handleFormInputChange(index, event)}
                      placeholder="Enter interval"
                    />
                    {inputErrors[index]?.interval ? (
                      <div className="error-message">
                        {inputErrors[index].interval || ''}
                      </div>
                    ) : null}
                    {/* {inputErrors[index]?.interval && <div className="error-message">{inputErrors[index].interval}</div>} */}
                  </div>
                ) : null}
              </div>
              {inputEntryFormFields?.length > 1
                &&
                <div>
                  <Button
                    onClick={() => handleRemoveInputField(index)}
                    className='outlined-delete-btn'
                  >
                    <DeleteOutlined className="delete-button" />
                  </Button>
                </div>
              }


            </div>
          ))}
          <div className="add-field-btn-container" style={{ marginTop: '5px' }}>
            <button
              className="outlined-primary-btn"
              type="button"
              onClick={handleAddInputEntryField}
            >
              <PlusOutlined className='plus-icon' />Add Input Column
            </button>
          </div>
        </div>
        <div>
          <h4 className='form-title'>Output Columns</h4>
          {outputEntryFormFields.map((field, index) => (
            <div className="dmn-form-container" key={index}>
              <div className="flex dmn-form-row">
                <div className="">
                  <div className="label">
                    Name
                  </div>
                  <Input
                    className="input-field-style"
                    type="text"
                    name="name"
                    value={field.name}
                    onChange={(event) => handleOutputFieldChange(index, event)}
                    placeholder="Enter Output Column"
                  />
                  {outputErrors[index]?.name ? (
                    <div className="error-message">
                      {outputErrors[index].name || ''}
                    </div>
                  ) : null}
                  {/* {outputErrors[index]?.name && <div className="error-message">{outputErrors[index].name}</div>} */}

                </div>
                <div className="">
                  <div className="label">
                    Type
                  </div>
                  <Select
                    showSearch
                    className="select-menu"
                    value={field.type || undefined}
                    placeholder="Select a type"
                    onChange={(value) => handleOutputFieldChange(index, { target: { name: 'type', value } })}
                  >
                    {/* <Option value="">Select a type</Option> */}
                    <Option value="string">String</Option>
                    <Option value="double">Decimal</Option>
                  </Select>
                  {outputErrors[index]?.type ? (
                    <div className="error-message">
                      {outputErrors[index].type || ''}
                    </div>
                  ) : null}
                  {/* {outputErrors[index]?.type && <div className="error-message">{outputErrors[index].type}</div>} */}

                </div>
              </div>
              {
                outputEntryFormFields?.length > 1
                &&
                <div>
                  <Button
                    onClick={() => handleRemoveOutputField(index)}
                    className='outlined-delete-btn'
                  >
                    <DeleteOutlined className="delete-button" />
                  </Button>
                </div>
              }

            </div>
          ))}
          <div className="add-field-btn-container">
            <Button
              className="outlined-primary-btn"
              onClick={handleAddOutputField}
            >
              <PlusOutlined className='plus-icon' />Add Output Column
            </Button>
          </div>
        </div>
      </div>
      <div className="form-submit-btn-container">
        <div style={{ marginBottom: '10px', color: rowCount > 180 ? 'orange' : 'inherit' }}>
          {rowCount > 0 && (
            <span>
              Rows to be generated: <b>{rowCount}</b> {rowCount > 180 && '(Close to 200 row limit!)'}
            </span>
          )}
        </div>
        <Button onClick={(e) => handleSubmit(e)} className="primary-btn" >
          Generate
        </Button>
      </div>
    </div>
  );
};

export default Form;
